import React, { createContext, useContext, useEffect, useState } from "react";
import {
  doc,
  onSnapshot,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../Admin/Config/Config";
import { onAuthStateChanged } from "firebase/auth";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [emptyCart, setEmptyCart] = useState(true);
  const [uid, setUid] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  useEffect(() => {
    onAuthStateChanged(auth, async(user) => {
      if (user) {
        setUid(user.uid);
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserDetails(userData);
        }
      } else {
        setUid(null);
      }
    });
  }, [uid]);
useEffect(() => {
  if (uid) {
    const cartRef = doc(db, "cart", uid);
    
    const unsubscribeCart = onSnapshot(cartRef, async (cartSnap) => {
      if (!cartSnap.exists()) {
        setCart([]);
        setEmptyCart(true);
        return;
      }

      const cartItems = cartSnap.data().items || [];
      if (cartItems.length === 0) {
        setCart([]);
        setEmptyCart(true);
        return;
      }

      // 🔹 Fetch product details for each item
       
      const updatedCart = await Promise.all(
        cartItems.map(async (item) => {
          if (!item?.productId) return null;
       let offer_price = 0;
        let original_price = 0;
        if (userDetails?.schemeId){
        const priceRef = doc(
          db,
          "product_price",
          userDetails?.schemeId,
          "products",
          item.productId
        );
        const priceSnap = await getDoc(priceRef);
        offer_price = priceSnap.exists() ? priceSnap.data().offer_price : 0;
        original_price = priceSnap.exists() ? priceSnap.data().original_price : 0;
        }
          const productRef = doc(db, "products", item.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            return {
              ...productSnap.data(),
              id: productSnap.id,
              productId: productSnap.id,
              cart_quantity: item.cart_quantity || 1,
              offer_price: offer_price || 0,
              original_price: original_price || 0,
              priceFlag: offer_price>0?true:false
            };
          }
          return null;
        })
      );
      setCart(updatedCart.filter(Boolean));
      setEmptyCart(updatedCart.filter(Boolean).length === 0);
    });

    return () => unsubscribeCart();
  } else {
    // ✅ Guest user → localStorage
    const loadCart = async () => {
      const localCart = JSON.parse(localStorage.getItem("cart")) || [];
      if (localCart.length === 0) {
        setCart([]);
        setEmptyCart(true);
        return;
      }

      // 🔹 Fetch product details for guest cart
      const updatedCart = await Promise.all(
        localCart.map(async (item) => {
          if (!item?.productId) return null; // ✅ Guard
          const productRef = doc(db, "products", item.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            return {
              ...productSnap.data(),
              id: productSnap.id,
              productId: productSnap.id,
              cart_quantity: item.cart_quantity || 1,
            };
          }
          return null;
        })
      );

      setCart(updatedCart.filter(Boolean));
      setEmptyCart(updatedCart.filter(Boolean).length === 0);
    };

    loadCart();

    window.addEventListener("storage", loadCart);
    window.addEventListener("cartUpdated", loadCart);

    return () => {
      window.removeEventListener("storage", loadCart);
      window.removeEventListener("cartUpdated", loadCart);
    };
  }
}, [uid,userDetails]);


  // 🔹 Add item to cart
  // 🔹 Add item to cart
  const addToCart = async (user, product) => {
    const productId = product.id;
    if (!product) {
      console.log(`NOT FOUND ${user}`);
      return;
    }
    if (uid) {
      const cartRef = doc(db, "cart", uid);
      const cartSnap = await getDoc(cartRef);

      let items = [];
      if (cartSnap.exists()) {
        items = cartSnap.data().items || [];
      }

      const existingIndex = items.findIndex((i) => i.productId === productId);
      if (existingIndex !== -1) {
        items[existingIndex].cart_quantity += product.cart_quantity;
      } else {
        items.push({ productId, cart_quantity: product.cart_quantity });
      }

      await setDoc(cartRef, { items }, { merge: true });
    } else {
      let localCart = JSON.parse(localStorage.getItem("cart")) || [];
      const existing = localCart.find((item) => item.productId === productId);

      if (existing) {
        existing.cart_quantity += 1;
      } else {
        localCart.push({ productId, cart_quantity: 1 });
      }

      localStorage.setItem("cart", JSON.stringify(localCart));
      window.dispatchEvent(new Event("cartUpdated"));
    }
  };

  // 🔹 Update item quantity
  const updateQuantity = async (productId, quantity) => {
    if (uid) {
      const cartRef = doc(db, "cart", uid);
      const cartSnap = await getDoc(cartRef);
      if (!cartSnap.exists()) return;

      let items = cartSnap.data().items || [];

      if (quantity > 0) {
        items = items.map((i) =>
          i.productId === productId ? { ...i, cart_quantity: quantity } : i
        );
      } else {
        items = items.filter((i) => i.productId !== productId);
      }

      await setDoc(cartRef, { items }, { merge: true });
    } else {
      let localCart = JSON.parse(localStorage.getItem("cart")) || [];

      if (quantity > 0) {
        localCart = localCart.map((i) =>
          i.productId === productId ? { ...i, cart_quantity: quantity } : i
        );
      } else {
        localCart = localCart.filter((i) => i.productId !== productId);
      }

      localStorage.setItem("cart", JSON.stringify(localCart));
      window.dispatchEvent(new Event("cartUpdated"));
    }
  };

  // 🔹 Remove item
  const removeFromCart = async (productId) => {
    if (uid) {
      const cartRef = doc(db, "cart", uid);
      const cartSnap = await getDoc(cartRef);
      if (!cartSnap.exists()) return;

      const items = cartSnap
        .data()
        .items.filter((i) => i.productId !== productId);

      await setDoc(cartRef, { items }, { merge: true });
    } else {
      const localCart = JSON.parse(localStorage.getItem("cart")) || [];
      const updatedCart = localCart.filter((i) => i.productId !== productId);

      localStorage.setItem("cart", JSON.stringify(updatedCart));
      window.dispatchEvent(new Event("cartUpdated"));
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        emptyCart,
        addToCart,
        updateQuantity,
        removeFromCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
